import React from "react";
import { View, StyleSheet, type StyleProp, type ViewStyle } from "react-native";
import Svg, { Path } from "react-native-svg";

/**
 * Properties for the FeedbackIcon component.
 */
export interface IFeedbackIconProps {
  /** Dimension in points (width and height). Defaults to 22. */
  size?: number;
  /** Forecolour of the speech bubble and thumbs up */
  color?: string;
  /** Whether to render as solid filled bubble with knockout thumb */
  filled?: boolean;
  /** Optional knockout color when filled is true. Defaults to #FFFFFF */
  knockoutColor?: string;
  /** Style overrides for the outer container */
  style?: StyleProp<ViewStyle>;
}

/**
 * Feedback icon rendering a thumbs up inside a rounded speech bubble.
 * Uses high-clarity custom vector SVG paths optimized for legibility at small sizes.
 *
 * @param {IFeedbackIconProps} props - Component properties.
 * @returns {React.JSX.Element} Rendered vector icon.
 */
export function FeedbackIcon({
  size = 22,
  color = "#000000",
  filled = false,
  knockoutColor = "#FFFFFF",
  style,
}: IFeedbackIconProps) {
  const thumbColor = filled ? knockoutColor : color;

  return (
    <View style={[styles.container, { width: size, height: size }, style]}>
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        {/* Rounded speech bubble with corner radius and speech tail */}
        <Path
          d="M5 3h14a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3H9l-5 4v-4H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3z"
          stroke={color}
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill={filled ? color : "none"}
        />
        {/* Crisp silhouette thumbs up centered in bubble cavity */}
        <Path
          d="M8 9.2v4.6h1.8V9.2H8zm2.2 4.6h3.4a1.3 1.3 0 0 0 1.2-.9l.7-2a.9.9 0 0 0-.9-1.1h-1.8V6.5a1.2 1.2 0 0 0-2.4 0v2.7"
          fill={thumbColor}
        />
      </Svg>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "center",
  },
});


